import React, { useState, useEffect } from 'react';
import { getLogoUrl, getContact } from './websiteStyles';

const INK = '#14161C';
const INK3 = '#8B8D94';
const LINE = '#E5E5E3';
const BLUE = '#2563EB';
const PAPER = '#FAFAF8';
const heading = '"Inter Tight", sans-serif';
const mono = '"JetBrains Mono", monospace';

function WebsiteHeader({ currentPage, onNavigate, onLogin }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const logoUrl = getLogoUrl();
  const contact = getContact();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const links = [
    { id: 'website', label: 'Home' },
    { id: 'website-used-cars', label: 'Used Cars' },
    { id: 'website-spare-parts', label: 'Spare Parts' },
    { id: 'website-about', label: 'About' },
    { id: 'website-contact', label: 'Contact' },
  ];

  const nav = (p) => { onNavigate(p); setMenuOpen(false); };
  const isActive = (id) => currentPage === id || (id === 'website-used-cars' && currentPage === 'website-inventory');

  return (
    <header style={{
      position: 'sticky', top: 0, zIndex: 50, transition: 'all 0.25s',
      background: scrolled ? 'rgba(250,250,248,0.92)' : PAPER,
      backdropFilter: scrolled ? 'blur(16px) saturate(1.6)' : 'none',
      borderBottom: `1px solid ${scrolled ? LINE : 'transparent'}`,
    }}>
      <div style={{ maxWidth: 1440, margin: '0 auto', padding: '0 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 64 }}>
        {/* Logo */}
        <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }} onClick={() => nav('website')}>
          {logoUrl ? (
            <img src={logoUrl} alt="BigStar" style={{ height: 32, objectFit: 'contain' }} />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <svg viewBox="0 0 28 28" style={{ width: 28, height: 28 }}>
                <rect width="28" height="28" rx="6" fill={INK}/>
                <text x="14" y="20" textAnchor="middle" fill="#fff" fontSize="18" fontWeight="bold" fontFamily="system-ui">★</text>
              </svg>
              <span style={{ fontFamily: heading, fontSize: 18, fontWeight: 700, letterSpacing: '-0.03em', color: INK }}>BigStar</span>
            </div>
          )}
        </div>

        {/* Desktop nav */}
        <nav style={{ display: 'flex', alignItems: 'center', gap: 2 }} className="ws-desktop-nav">
          {links.map(l => (
            <button key={l.id} onClick={() => nav(l.id)} style={{
              padding: '7px 16px', borderRadius: 9999, fontSize: 14, fontWeight: 500,
              fontFamily: heading, border: 'none', cursor: 'pointer', transition: 'all 0.2s',
              background: isActive(l.id) ? INK : 'transparent',
              color: isActive(l.id) ? '#fff' : INK3,
            }}
              onMouseEnter={e => { if (!isActive(l.id)) { e.target.style.color = INK; e.target.style.background = 'rgba(0,0,0,0.04)'; } }}
              onMouseLeave={e => { if (!isActive(l.id)) { e.target.style.color = INK3; e.target.style.background = 'transparent'; } }}>
              {l.label}
            </button>
          ))}
        </nav>

        {/* Desktop right actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }} className="ws-desktop-nav">
          {/* Language */}
          <button style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px', borderRadius: 9999, border: `1px solid ${LINE}`, background: '#fff', fontSize: 13, fontWeight: 500, color: INK, cursor: 'pointer', fontFamily: heading }}>
            <svg viewBox="0 0 24 24" fill="none" stroke={INK3} strokeWidth="1.5" style={{ width: 16, height: 16 }}><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>
            EN
          </button>
          {/* Sign in */}
          {onLogin && (
            <button onClick={onLogin} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', borderRadius: 9999, border: `1px solid ${LINE}`, background: '#fff', fontSize: 13, fontWeight: 500, color: INK, cursor: 'pointer', fontFamily: heading }}>
              <svg viewBox="0 0 24 24" fill="none" stroke={INK} strokeWidth="1.5" style={{ width: 16, height: 16 }}><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              Sign in
            </button>
          )}
          {/* Browse stock */}
          <button onClick={() => nav('website-used-cars')} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 18px', borderRadius: 9999, background: BLUE, color: '#fff', fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer', fontFamily: heading }}>
            Browse stock <span style={{ fontSize: 14 }}>→</span>
          </button>
        </div>

        {/* Mobile hamburger */}
        <button onClick={() => setMenuOpen(!menuOpen)} className="ws-mobile-menu" style={{ display: 'none', background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: INK, padding: 8 }}>
          {menuOpen ? '✕' : '☰'}
        </button>
      </div>

      {/* Mobile dropdown */}
      {menuOpen && (
        <div className="ws-mobile-dropdown" style={{ padding: '8px 20px 20px', borderTop: `1px solid ${LINE}`, background: PAPER }}>
          {links.map(l => (
            <button key={l.id} onClick={() => nav(l.id)} style={{
              display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none',
              padding: '14px 0', fontSize: 16, cursor: 'pointer', fontFamily: heading,
              fontWeight: isActive(l.id) ? 600 : 400,
              color: isActive(l.id) ? BLUE : INK,
              borderBottom: `1px solid ${LINE}`,
            }}>
              {l.label}
            </button>
          ))}
          {onLogin && (
            <button onClick={() => { onLogin(); setMenuOpen(false); }} style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '14px 0', fontSize: 16, cursor: 'pointer', fontFamily: heading, color: INK3 }}>
              Sign in
            </button>
          )}
          <button onClick={() => nav('website-used-cars')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', marginTop: 12, padding: 14, borderRadius: 10, background: BLUE, color: '#fff', fontWeight: 600, fontSize: 14, border: 'none', cursor: 'pointer', fontFamily: heading }}>
            Browse stock →
          </button>
        </div>
      )}

      <style>{`
        @media (max-width: 840px) { .ws-desktop-nav { display: none !important; } .ws-mobile-menu { display: block !important; } }
        @media (min-width: 841px) { .ws-mobile-dropdown { display: none !important; } }
      `}</style>
    </header>
  );
}

export default WebsiteHeader;
